/**
 * Netx.Model extends Backbone.Model, adding just a few handy methods.
 * @class
 * @extends Backbone.ES6Model
 */
// Setup ajv for data validation
import Ajv from 'ajv';
import Backbone from 'backbone';
import Rpc from 'core-libs/backbone.rpc';
import {URL} from '@netx/core-next/url';
import {protectObjectProxy} from '@netx/core/utils/protectObjectProxy';

// these are circular references
// import {batchJobs} from '@netx/core-app/collections/singleton/batchJobs';
// import {legacyWebsocket} from '@netx/core-app/collections/singleton/websocket';

// Wrap an optional error callback with a fallback error event.
// This is from the backbone.js file and allows us to override the save
// method so that we can support rejecting promises when validation fails
const wrapError = (model, options) => {
	const error = options.error;
	options.error = function (resp) {
		if (error) error.call(options.context, model, resp, options);
		model.trigger('error', model, resp, options);
	};
};

const ajv = new Ajv({
	allErrors: true,
	coerceTypes: true,
	$data: true,
	missingRefs: 'ignore',
	useDefaults: 'empty',
	nullable: true,
});

/**
 * Get default values from schema
 * Used to define `this.defaults`
 * @function
 * @param {schema} schema Schema
 * @return {object} Defaults
 */
const getDefaultsFromSchema = (schema) => {
	const {properties} = schema;
	const defaults = {};
	for (let key in properties) {
		if (properties.hasOwnProperty(key)) {
			const defaultValue = properties[key].default;
			if (typeof defaultValue !== 'undefined') {
				defaults[key] = defaultValue;
			}
		}
	}
	return defaults;
};

const BBModel = Backbone.Model;
// Make a Model we can extend from in other classes
class ES6Model {
	get schema() {
		return this.constructor.Schema;
	}

	/** @override */
	constructor(attrs, opts) {
		const schema = this.schema;
		if (schema) {
			// If we have not explicitely setup our own validation
			// validate via ajv and our provided schema
			if (!this.validate) {
				const ValidateSchema = ajv.compile(schema);
				this.validate = (attrs, opts) => {
					const isValid = ValidateSchema(attrs);
					return !isValid ? [...ValidateSchema.errors] : false;
				};
			}

			// Set our defaults based on the schema provided
			// If you are coming from the old backbone stuff and want to use schema and extend from another schema
			// you will need to do that in your schema file and not in the model "defaults" getter that we have
			// been using
			Object.defineProperty(this, 'defaults', {
				get() {
					return getDefaultsFromSchema(schema);
				},
				configurable: false,
			});
		}

		BBModel.call(this, attrs, opts);

		// Always represent `id` on the model's attributes regardless of what the key actually is
		// I have yet to find a model that is utilizing a key of id for anything but the id
		// DEVNOTE: Setting this after call to backbone model as `this.attributes` does not exist until then
		if (schema && schema.primary !== 'id') {
			Object.defineProperty(this.attributes, 'id', {
				get: () => {
					return this.id;
				},
				configurable: false,
			});
		}
	}
}
// Object.assign(ES6Model.prototype, BBModel.prototype);
_.extend(ES6Model.prototype, BBModel.prototype);
Backbone.ES6Model = ES6Model;

class Model extends ES6Model {
	/** @override */
	get idAttribute() {
		const schema = this.schema;
		// The backbone way (so that we do not have to do every model at once)
		// Any model that does not use `id` will already be overriding this getter
		if (!schema) return 'id';

		// The new schema way
		return schema.primary;
	}

	/** @type {boolean} */
	get isModel() {
		return this.constructor.__isModel__;
	}

	// Whelp
	// Need to fix in backbone.rpc - the model get's a property of `syncing`
	// whether the call is a syncable (read) or not
	// And since our loading indication relies on the `request:read` event and the `sync` event
	// we will get false loading positives by checking `this.syncing`
	// So, check for the `read` method in our current calls list
	get isSyncing() {
		return this._rpc && this.syncing && this._rpc.callsByName.read != null;
	}

	/**
	 * The attribute key to find the model's "title"
	 * @type {string}
	 */
	get titleAttribute() {
		return 'title';
	}

	get url() {
		return URL.api;
	}

	// TODO: when we have switched all the way over to the schema pattern we can remove this
	get defaults() {
		return {};
	}

	/** @type {array} */
	get clientAttrs() {
		return [];
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {};
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {};
	}

	/** @type {object} */
	get methods() {
		return {};
	}

	/** @type {object} */
	get parsers() {
		return {};
	}

	/**
	 * @method
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		// Init backbone.rpc if requested:
		const rpc = opts.rpc != null ? opts.rpc : this.rpc;
		if (rpc === true) {
			this._rpc = new Rpc();
		}

		/**
		 * When a stored model should expire
		 * Set to true to persist until explicitly deleted
		 * https://github.com/ozantunca/locally
		 * Forever/Milliseconds/TTL
		 * @type {boolean|integer|string}
		 * @default
		 * @see Netx.Collection
		 */
		this.storeOptions = {
			persist: false,
		};
		// Store options
		if (opts && _.isObject(opts.storeOptions)) {
			this.storeOptions = _.extend({}, this.storeOptions, opts.storeOptions);
			delete opts.storeOptions;
		}

		/**
		 * If we defined the object _unsavedChanges on a Netx.Model, that
		 * object will track every change between syncs.  This is useful
		 * for tracking unsaved local changes over time.
		 */
		// On change events, store the changes.
		this.listenTo(this, 'change', (model, options) => {
			if (!_.isObject(this._unsavedChanges)) return;
			if (options && options.silent) return;
			_.extend(this._unsavedChanges, model.changedAttributes());
		});
		// On sync events, flush the changes.
		this.listenTo(this, 'sync', () => {
			this.syncedOnce = true;
			if (_.isObject(this._unsavedChanges)) {
				this._unsavedChanges = {};
			}
		});

		//////////////////////////////////////////
		// HOUSEKEEPING
		//////////////////////////////////////////

		// Where will store references to the collection we belong to. We use the _listenId and store only that
		// That way we are not creating any circular references and do not risk making our garbage collection
		// even more difficult to deal with in regards to our ghost models
		// On one hand- it is great to see details about the collection the model claims to belong to in cases
		// where you are not sure why it still belongs to one - but the cost of storing the actual collection
		// coul be that neither the model nor the collection ever get collected as the model is listening to some
		// global object and maintiaining a reference to a collection that should have been garbage collected
		// which then could be referencing other objects that would have otherwise been garbage collected and so on.
		// this.debugCollections = [];
		this.collections = [];
		// Backbone never really anticipated (on purpose) that models
		// would create models and listen to other things (not self) (especially global variables like our dispatcher)
		// Cleanup
		this.listenTo(this, 'remove', (model, collection, opts) => {
			// Each model that creates sub models should be listening for remove and then triggering the same on its sub models
			// That way everything gets cleaned up and memory leaks decline
			this.removeCollectionReference(collection);
		});
		this.listenTo(this, 'add', (model, collection, opts) => {
			// If we are not already storing a reference - store one
			this.addCollectionReference(collection);
		});
		//////////////////////////////////////////
		// !HOUSEKEEPING
		//////////////////////////////////////////
	}

	/**
	 * @method
	 * @param {string} perm Permission
	 * @return {boolean} Allowed
	 */
	allows(perm) {
		if (perm === 'read' || perm === 'r' || perm === 'download') return true;

		var permitted = this.get('permission');

		// Nothing we can check
		if (!permitted) return true;

		switch (perm) {
			case 'd':
			case 'delete':
				return permitted === 'delete' || permitted === 'd';
			case 'modify':
			case 'w':
			case 'edit':
			case 'write':
				return (
					permitted === 'delete' ||
					permitted === 'write' ||
					permitted === 'd' ||
					permitted === 'w'
				);
		}

		return false;
	}

	/**
	 * @override
	 * @param {Netx.Models.BatchJob} Batch job model
	 */
	batchJobComplete(batchJob) {
		var args = batchJob.get('args') || [];
		this.trigger.apply(this, ['complete', this, batchJob].concat(args));
	}
	/**
	 * @override
	 * @param {Netx.Models.BatchJob} Batch job model
	 * @param {string} error Error
	 */
	batchJobError(batchJob, error) {
		var args = batchJob.get('args') || [];
		this.trigger.apply(this, ['error', this, error].concat(args));
	}
	/**
	 * @override
	 * @param {Netx.Models.BatchJob} Batch job model
	 * @param {number} progress Progress
	 */
	batchJobProgress(batchJob, progress) {
		var args = batchJob.get('args') || [];
		this.trigger.apply(
			this,
			['progress', this, batchJob, progress].concat(args),
		);
	}

	/**
	 * There is a fun labels game we play with some of our objects; particularly users
	 * We need to get our labels on the same page as what we might get from an autolookup
	 * This method allows less than ideal models to modify how this label is gathered
	 * @method
	 * @return {string} Atuocomplete label
	 */
	getAutocompleteLabel() {
		return this.getLabel();
	}

	/**
	 * @method
	 * @param {string} method Method name
	 */
	getRestLoadedMessage(method) {
		var messages = this.xhrLoadedMessages || {};
		return messages[method];
	}
	/**
	 * @method
	 * @param {string} method Method name
	 */
	getRestLoadingMessage(method) {
		var messages = this.xhrLoadingMessages || {};
		return messages[method];
	}

	/**
	 * this.self() returns just the attributes of a model that are reflected in the DAM;
	 * it is used in method definitions to omit model attributes that should *not* be
	 * synced.  We use it for cases where the data is structured differently in the
	 * model than on the wire, and for models that want to have additional attributes
	 * for local-only reasons.
	 *
	 * The default implementation removes any/all client-only attributes defined in the optional array property this.clientAttrs,
	 * and aggregates ny/all attributes defined in the optional array property this.gooAttrs
	 * into a single attribute called 'goo'.  If neither of those optional properties
	 * is defined, this.self() returns all model attributes.
	 * @return {object}
	 * @method
	 */
	self() {
		// If we have goo attrs, this will pack them.  Otherwise it will return a clone of this.attributes.
		let attrs = this.packAttrs();

		const schema = this.schema;
		// If we have client-only attrs, omit them.
		// The new schema way
		if (schema) {
			attrs = _.omit(attrs, schema.clientSide);
		}
		// The old way
		else if (this.clientAttrs) {
			attrs = _.omit(attrs, this.clientAttrs);
		}
		// Cloning so we don't accidentally modify model properties while preparing attributes to be sent to RPC methods
		return _.clone(attrs);
	}

	/**
	 * this.packAttrs(), given an attributes object (usually this.attributes), returns the JSON string
	 * representation of any/all model properties listed in this.gooAttrs.
	 * It's used by this.self, when converting from the client-side to the server-side representation.
	 * @param {string} goo an attributes object.  If undefined, this.attributes is used.
	 * @return {string}
	 * @method
	 */
	packAttrs(ungooeyObj) {
		ungooeyObj = ungooeyObj || this.attributes;
		if (this.gooAttrs) {
			var clientGoo = JSON.stringify(_.pick(ungooeyObj, this.gooAttrs));

			// HACK around bugs in the DAM JSON parser: triple-escape escaped quotes!
			var serverGoo = clientGoo.replace(/"/g, '\\"');

			return _.extend({}, _.omit(ungooeyObj, this.gooAttrs), {goo: serverGoo});
		} else {
			return ungooeyObj;
		}
	}

	/**
	 * this.unpackAttrs(), given an object with packed goo, returns an unpacked version.
	 * It's used by parsers converting from server-side to client-side representations.
	 * @param {object} gooeyObj the goo-encoded object
	 * @return {object} the goo-decoded version.
	 */
	unpackAttrs(gooeyObj) {
		if (!(this.gooAttrs && gooeyObj && gooeyObj.goo)) {
			// do nothing if model defines no goo.
			return gooeyObj;
		}

		var serverGoo = gooeyObj.goo;
		// HACK around bugs in the DAM JSON parser: unescape triple-escaped quotes!
		var clientGoo = serverGoo.replace(/\\"/g, '"');

		// with _.omit, delete the goo key
		return _.omit(
			_.extend(
				{},
				gooeyObj,
				// with _.pick, only allow goo we've defined in .gooAttrs!
				_.pick(JSON.parse(clientGoo), this.gooAttrs),
			),
			'goo',
		);
	}

	/**
	 * Overriding because we expect to get back a promise
	 * when validation or an error in set occurs backbone just returns false
	 * @override
	 */
	save(key, val, options) {
		// Handle both `"key", value` and `{key: value}` -style arguments.
		var attrs;
		if (key == null || typeof key === 'object') {
			attrs = key;
			options = val;
		} else {
			(attrs = {})[key] = val;
		}

		options = _.extend({validate: true, parse: true}, options);
		var wait = options.wait;

		// If we're not waiting and attributes exist, save acts as
		// `set(attr).save(null, opts)` with validation. Otherwise, check if
		// the model will be valid when the attributes, if any, are set.
		if (attrs && !wait) {
			if (!this.set(attrs, options)) {
				const error = new Error(`Error setting data`);
				return new $.Deferred().reject(error);
				// return false;
			}
		} else if (!this._validate(attrs, options)) {
			const error = new Error(`Validation error`);
			error.details = this.validationError;
			return new $.Deferred().reject(error);
			// return false;
		}

		// After a successful server-side save, the client is (optionally)
		// updated with the server-side state.
		var model = this;
		var success = options.success;
		var attributes = this.attributes;
		options.success = function (resp) {
			// Ensure attributes are restored during synchronous saves.
			model.attributes = attributes;
			var serverAttrs = options.parse ? model.parse(resp, options) : resp;
			if (wait) serverAttrs = _.extend({}, attrs, serverAttrs);
			if (serverAttrs && !model.set(serverAttrs, options)) return false;
			if (success) success.call(options.context, model, resp, options);
			model.trigger('sync', model, resp, options);
		};
		wrapError(this, options);

		// Set temporary attributes if `{wait: true}` to properly find new ids.
		if (attrs && wait) this.attributes = _.extend({}, attributes, attrs);

		var method = this.isNew() ? 'create' : options.patch ? 'patch' : 'update';
		if (method === 'patch' && !options.attrs) options.attrs = attrs;
		var xhr = this.sync(method, this, options);

		// Restore attributes.
		this.attributes = attributes;

		return xhr;
	}

	/**
	 * Keep track of any models or collections that may be attributes on the model
	 * @override
	 */
	set(key, val, options) {
		if (_.isObject(key)) {
			_.each(
				key,
				function (v, k) {
					if (!v) return;

					if (v instanceof this.constructor) {
						this.addSubModel(v);
					} else if (v && v.isModel) {
						this.addSubCollection(v);
					}
				},
				this,
			);
		} else if (val) {
			if (val instanceof this.constructor) {
				this.addSubModel(val);
			} else if (val.isCollection) {
				this.addSubCollection(val);
			}
		}

		return super.set(key, val, options);
	}

	/**
	 * @method
	 * @return {object} Defaults
	 */
	getDefaults() {
		return this.constructor.GetDefaults();
	}

	/**
	 * @method
	 * @return {number}
	 */
	getIndex() {
		return this.collection ? this.collection.indexOf(this) : -1;
	}

	/**
	 * A general-purpose function for getting a UI-compliant label for an object.
	 * This function can & should be overridden in all child classes.
	 * @method
	 * @return {string} Label (title)
	 */
	getLabel() {
		return this.get(this.titleAttribute);
	}

	/**
	 * Object Polling:
	 * pollUntil() tells any model/collection to do something repeatedly until a conditional test passes,
	 * emitting appropriate events along the way.  Use this to follow DAM changes to certain object properties,
	 * or to check status until a status change.
	 *
	 * @param {function} doneTest          A function that returns true if polling should finish.  (Required)
	 * @param {string}   eventPrefix       Event string prefix for polling events.  If undefined, no events are triggered.
	 * @param {function} pollMethod        A function that causes a poll.  Must return a promise.  Defaults to this.fetch;
	 * @param {number}   pollInterval      Milliseconds between polls.  Defaults to 500.
	 * @param {object}   pollMethodOptions Options to pass to the RPC method
	 * @method
	 * @todo convert params to a single object so that we can add/remove options without needing to modify every piece of code that utilizes this method
	 * (you know, unless they NEED to be updated)
	 */
	pollUntil(
		doneTest,
		eventPrefix,
		pollMethod,
		pollInterval,
		pollMethodOptions,
	) {
		// Vars
		var d = new $.Deferred(),
			self = this;

		if (!_.isFunction(doneTest)) {
			throw new Error('pollUntil: doneTest argument is not a function.');
		}

		pollMethodOptions = pollMethodOptions || {};
		pollInterval = pollInterval || 500; // ms

		pollMethod =
			pollMethod ||
			function () {
				if (_.isFunction(self.fetch)) return self.fetch(pollMethodOptions);
				throw new Error('default poll method (fetch) not defined.');
			};

		if (this._polling) {
			// We are already polling.
			console.warn('This instance is already polling');
			return this._polling.promise();
		}

		// Method for when we're finished:
		var finished = function () {
			self._polling.resolveWith(self);
			if (eventPrefix) {
				dispatcher.trigger.apply(
					dispatcher,
					[
						eventPrefix +
							(self._stopPolling ? ':stoppedPolling' : ':donePolling'),
					].concat(_.toArray(arguments)),
				);
			}
			delete self._polling;
			delete self._pausedPolling;
			delete self._stopPolling;
		};

		try {
			// Pass undefined so that we may differentiate between the first test and the rest
			if (doneTest.apply(this, [undefined])) {
				// We don't even need to poll.
				finished();
				return d.promise();
			}
		} catch (ex) {
			// doneTest() might fail without proper arguments.  If so, assume that means 'false'.
			// That means we're going to have to poll at least once in order to perform this test.
		}

		var loop = function () {
			if (self._pausedPolling) {
				_.delay(loop, pollInterval);
				return;
			}

			pollMethod.call(self, pollMethodOptions).then(
				function (response) {
					// For those cases that the server sends an error as a success
					if (_.isObject(response) && response.error) {
						console.warn(
							'Server sent error as success. \nModule :',
							self.module,
							'\nModel  :',
							self,
						);
						return d.reject(
							new Error(response.error.message || response.error),
						);
					}
					if (eventPrefix) {
						dispatcher.trigger.apply(
							dispatcher,
							[eventPrefix + ':polled'].concat(_.toArray(arguments)),
						);
					}
					if (this._stopPolling || doneTest.apply(self, arguments)) {
						// Done!
						finished.apply(self, arguments);
					} else {
						_.delay(loop, pollInterval);
					}
					return;
				},
				function (err) {
					if (self._pausedPolling) {
						_.delay(loop, pollInterval);
						return;
					}
					d.reject(err);
					if (eventPrefix) {
						dispatcher.trigger.apply(
							dispatcher,
							[eventPrefix + ':errorPolling', err.message].concat(
								_.toArray(arguments),
							),
						); // use instead (becuase verbs are better?)
						// This would be nicer & more right:
						// dispatcher.applyTrigger( eventPrefix + ':errorPolling', arguments);
					}
					delete this._polling;
					delete this._pausedPolling;
				},
			);
		};

		// Save the deferred as this._polling, to show we're polling.
		this._polling = d;
		if (eventPrefix) {
			dispatcher.trigger.apply(
				dispatcher,
				[eventPrefix + ':startedPolling'].concat(_.toArray(arguments)),
			);
		}
		loop();

		return this._polling.promise();
	}

	pausePolling() {
		if (this._polling) {
			this._pausedPolling = true;
		}

		return this;
	}

	resumePolling() {
		if (this._polling) {
			this._pausedPolling = false;
		}

		return this;
	}

	/**
	 * stopPolling() halts any poll loop currently in process.
	 */
	stopPolling() {
		if (this._polling) {
			this._stopPolling = true;
		}

		return this;
	}

	/**
	 * @method
	 * @param {string} uuid UUID
	 * @param {array} args Arguments
	 * @return {Netx.Models.BatchJob}
	 * @fires module:app#app:pollBatchJob
	 */
	trackBatchJob(uuid, args, pollInterval) {
		// Poll
		var batchJob = Netx.batchJobs.get(uuid);
		if (!batchJob) {
			dispatcher.trigger(
				'app:pollBatchJob',
				uuid,
				null,
				false,
				args,
				pollInterval,
			);
			batchJob = Netx.batchJobs.get(uuid);
		}
		if (batchJob) {
			this.listenToOnce(batchJob, 'complete', function (batchJob) {
				this.stopListening(batchJob);
				this.batchJobComplete(batchJob);
			});
			this.listenToOnce(batchJob, 'error', function (batchJob, error) {
				this.stopListening(batchJob);
				this.batchJobError(batchJob, error);
			});
			this.listenTo(
				batchJob,
				'change:percentComplete',
				function (batchJob, progress) {
					this.batchJobProgress(batchJob, progress);
				},
			);
		}
		return batchJob;
	}

	/**
	 * A scoped version of jQuery.when(), that always uses 'this' as the context for the promise it returns.
	 * It's also able to accept an array of promises, so we don't need to use .apply to make it go.
	 */
	when() {
		// Vars
		var d = new $.Deferred(),
			self = this,
			resolve = function () {
				d.resolveWith(self, arguments);
			},
			reject = function () {
				d.rejectWith(self, arguments);
			},
			progress = function () {
				d.notifyWith(self, arguments);
			};

		// If the first argument is an array of promises, we ignore the others.
		var arg0 = _.first(arguments);
		if (_.isArray(arg0) && arg0.length > 0) {
			$.when.apply($, arg0).then(resolve, reject, progress);
		} else {
			$.when.apply($, arguments).then(resolve, reject, progress);
		}

		return d.promise();
	}

	//////////////////////////////////////////
	// HOUSEKEEPING
	//////////////////////////////////////////
	/**
	 * Keep track of the collections I belong to
	 * @method
	 * @param {Netx.Collection} collection Collection
	 */
	addCollectionReference(collection) {
		if (collection && this.collections) {
			if (_.indexOf(this.collections, collection._listenId) < 0) {
				if (!collection._listenId) return;
				this.collections.push(collection._listenId);
				// this.debugCollections[ collection._listenId ] = collection;
			}
		}
	}
	/**
	 * Keep track of the collections I belong to
	 * @method
	 * @param {Netx.Collection} collection Collection
	 */
	removeCollectionReference(collection) {
		if (this.collections) {
			if (collection) {
				var idx = _.indexOf(this.collections, collection._listenId);
				if (idx > -1) {
					this.collections.splice(idx, 1);
					// delete this.debugCollections[ collection._listenId ];
				}
			}
			// Give it time and see if we may have been added to another collection before checking length
			_.defer(
				_.bind(function () {
					if (!this.collections.length) {
						this.garbageCollect();
					}
				}, this),
			);
		}
	}
	/**
	 * Call this when manually adding a sub collection
	 * @method
	 * @param {Netx.Collection} collection Collection to track
	 */
	addSubCollection(collection) {
		if (collection) {
			if (!_.isObject(this.subCollections)) this.subCollections = {};
			// In very rare situations some collections do not have their _listenId yet
			// Defer seems to solve it and I cannot recreate any further
			_.defer(
				_.bind(function () {
					if (
						this.subCollections &&
						_.isUndefined(this.subCollections[collection._listenId])
					) {
						this.subCollections[collection._listenId] = collection;
					}
				}, this),
			);
		}
		return this;
	}
	/**
	 * Call this when manually removing a sub collection
	 * @method
	 * @param {Netx.Collection} collection Collection to cleanup
	 */
	removeSubCollection(collection) {
		if (
			collection &&
			collection._listenId &&
			!_.isUndefined(this.subCollections[collection._listenId])
		) {
			collection.garbageCollect();
			delete this.subCollections[collection._listenId];
		}
		return this;
	}
	/**
	 * Call this when manually adding a sub model
	 * @method
	 * @param {Netx.Model} model Model to track
	 */
	addSubModel(model) {
		if (!_.isObject(this.subModels)) this.subModels = {};
		if (_.isUndefined(this.subModels[model._listenId])) {
			this.subModels[model._listenId] = model;
		}
		return this;
	}
	/**
	 * Call this when manually removing a sub model
	 * @method
	 * @param {Netx.Model} model Model to cleanup
	 */
	removeSubModel(model) {
		if (
			model &&
			model._listenId &&
			!_.isUndefined(this.subModels[model._listenId])
		) {
			// Hotfix for DAM-6402 -- revisit in 8.6 .  This solves a mistaken assumption about references,
			// but it un-solves a memory leak ...
			// model.trigger('remove', model, null, null);
			delete this.subModels[model._listenId];
		}
		return this;
	}

	/**
	 * Subscribe to websocket
	 * @method
	 */
	subscribe() {
		if (!this.socket) {
			if (this.roomName == null) {
				throw new Error(
					'`this.roomName` is not set. Cannot subscribe to websocket channel.',
				);
			}

			var socket = (this.socket = Netx.websocket);
			this.listenTo(
				socket,
				'socket:message:' + this.roomName,
				this.subscriptionMessage,
			);
			socket.subscribe(this.roomName);
		}

		return this;
	}

	/**
	 * Message received from websocket channel
	 * @method
	 * @param {object} data Message data
	 */
	subscriptionMessage(data) {
		switch (data.action) {
			case 'edit':
				this.set(data.dto);
				break;
		}
	}

	/**
	 * Unsubscribe from websocket
	 * @method
	 */
	unsubscribe() {
		if (this.socket) {
			if (this.roomName == null) {
				throw new Error(
					'`this.roomName` is not set. Cannot unsubscribe from websocket channel.',
				);
			}

			this.socket.unsubscribe(this.roomName);
			this.stopListening(this.socket);
			this.socket = false;
		}
		return this;
	}

	/**
	 * Severs all ties to the world so it can be eligible for garbage collection
	 * @method
	 */
	garbageCollect() {
		this._rpc && this._rpc.garbageCollect();

		// Let go of any websocket channels
		this.unsubscribe();
		// Let go of event listeners
		this.stopListening().off();
		this.clear();

		// Loop over sub models and trigger their cleanup
		if (_.isObject(this.subModels)) {
			// Hotfix for DAM-6402 -- ditto to above.
			// _.each( this.subModels, function( m ) {
			// 	m.trigger('remove', m, null, null);
			// } );
			delete this.subModels;
		}
		// Loop over sub collection and trigger their cleanup
		if (_.isObject(this.subCollections)) {
			_.each(this.subCollections, function (c) {
				c.garbageCollect();
			});
			delete this.subCollections;
		}

		this.garbageCollected = true;

		return this;
	}
	/**
	 * Backbone.Model.destroy() returns false -- not a promise -- if the model is new;
	 * it presumes the server doesn't have the model, and doesn't need to be contacted.
	 *
	 * That's reasonable ... but every other sync method always returns a promise,
	 * and we've written all our code presuming destroy() always returns a promise,
	 * so at this point we're making it Netx policy that Netx.Model.destroy()
	 * _always_ returns a promise.
	 *
	 * @param {objct} opts sync args
	 * @returns {Promise}
	 */
	destroy(opts) {
		const retVal = super.destroy(opts);
		if (!_.isPromise(retVal)) {
			return this.when(); // a pre-resolved promise with correct context.
		} else {
			return retVal;
		}
	}
	//////////////////////////////////////////
	// !HOUSEKEEPING
	//////////////////////////////////////////

	/**
	 * Our defaults could be an object or a function that returns an object
	 * Do not make others figure this out everytime the defaults are neeeded
	 * @method
	 * @return {object} Model defaults (cloned so you do not risk polluting the prototype)
	 */
	static GetDefaults() {
		if (!this.Schema) return this.prototype.defaults;
		return getDefaultsFromSchema(this.Schema);
	}

	/**
	 * The attribute key to find the model's "title"
	 * @type {string}
	 */
	static get TitleAttribute() {
		return this.prototype.titleAttribute;
	}

	/**
	 * Netx.Model.*.Load() (experimental) takes a unique ID and converts into a server populated model instance.
	 * It returns a promise for the eventual object.
	 * @param {number} id Id that corresponds to the model's idAttribute.
	 * @todo Load() would be handy for a wide variety of collections.
	 * To get it to all of them we'd need to add the Load class method to Netx.Collection.
	 */
	static Load(id) {
		// Setting the idAttribute on instantiation does not work in Backbone.
		// So we must explicitly set idAttribute after the instance has been created
		const a = new this();
		const idAttr = this.prototype.idAttribute || 'id';

		return a
			.set(idAttr, id)
			.fetch()
			.then(() => {
				return a;
			});
	}

	/**
	 * @method
	 * @param {schema} schema Schema
	 */
	static SetSchema(schema) {
		Object.defineProperty(this, 'Schema', {
			value: protectObjectProxy(schema),
			configurable: false,
			writable: false,
		});
	}

	/** @type {boolean} */
	static get __isModel__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'Model';
	}
}

export default Model;
